
/**
 * Formats HTML content to properly handle special characters and escape sequences
 * @param html The HTML content to format
 * @returns Properly formatted HTML
 */
export function formatHtml(html: string): string {
  if (!html) return '';
  
  return html
    .replace(/'/g, "'")
    .replace(/"/g, '"')
    .replace(/—/g, '-')
    .replace(/–/g, '-')
    .replace(/\.\.\./g, '…');
}

/**
 * Processes markdown-style formatting in text and converts it to HTML
 * @param text Text with markdown formatting
 * @returns HTML formatted text
 */
export function markdownToHtml(text: string): string {
  if (!text) return '';
  
  // Replace markdown syntax with HTML
  return formatHtml(text)
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br />')
    .replace(/^/, '<p>')
    .replace(/$/, '</p>');
}

/**
 * Processes special emoji markers in text and converts them to styled HTML
 * @param text Text with emoji markers
 * @returns HTML with styled elements
 */
export function processSpecialMarkers(text: string): string {
  if (!text) return '';
  
  // Process special emoji markers with enhanced styling
  return text
    .replace(/✅(.*?)(?=\n|$)/g, '<div class="success-block"><span class="emoji">✅</span>$1</div>')
    .replace(/❌(.*?)(?=\n|$)/g, '<div class="error-block"><span class="emoji">❌</span>$1</div>')
    .replace(/💡(.*?)(?=\n|$)/g, '<div class="tip-block"><span class="emoji">💡</span>$1</div>')
    .replace(/🚀(.*?)(?=\n|$)/g, '<div class="advanced-block"><span class="emoji">🚀</span>$1</div>')
    .replace(/🎯(.*?)(?=\n|$)/g, '<div class="memory-trick-block bg-amber-50 dark:bg-amber-900/30 p-3 rounded-lg my-2"><span class="emoji">🎯</span><span class="font-medium">Memory Trick: </span>$1</div>')
    .replace(/🔄(.*?)(?=\n|$)/g, '<div class="quick-recall-block bg-blue-50 dark:bg-blue-900/30 p-3 rounded-lg my-2"><span class="emoji">🔄</span><span class="font-medium">Quick Recall: </span>$1</div>')
    .replace(/🎭(.*?)(?=\n|$)/g, '<div class="story-block bg-purple-50 dark:bg-purple-900/30 p-3 rounded-lg my-2"><span class="emoji">🎭</span><span class="font-medium">Story Bridge: </span>$1</div>')
    .replace(/🎪(.*?)(?=\n|$)/g, '<div class="visual-analogy-block bg-green-50 dark:bg-green-900/30 p-3 rounded-lg my-2"><span class="emoji">🎪</span><span class="font-medium">Visual Analogy: </span>$1</div>')
    .replace(/🎮(.*?)(?=\n|$)/g, '<div class="interactive-note bg-indigo-50 dark:bg-indigo-900/30 p-3 rounded-lg my-2"><span class="emoji">🎮</span><span class="font-medium">Mini Challenge: </span>$1</div>')
    .replace(/🌟(.*?)(?=\n|$)/g, '<div class="key-insight-block bg-rose-50 dark:bg-rose-900/30 p-3 rounded-lg my-2"><span class="emoji">🌟</span><span class="font-medium">Key Insight: </span>$1</div>')
    .replace(/📝(.*?)(?=\n|$)/g, '<div class="practice-block bg-cyan-50 dark:bg-cyan-900/30 p-3 rounded-lg my-2"><span class="emoji">📝</span><span class="font-medium">Quick Practice: </span>$1</div>')
    .replace(/🔍(.*?)(?=\n|$)/g, '<div class="common-error-block bg-red-50 dark:bg-red-900/30 p-3 rounded-lg my-2"><span class="emoji">🔍</span><span class="font-medium">Watch Out: </span>$1</div>')
    // New memory enhancement formats
    .replace(/🧠(.*?)(?=\n|$)/g, '<div class="neural-connection-block bg-violet-50 dark:bg-violet-900/30 p-3 rounded-lg my-2"><span class="emoji">🧠</span><span class="font-medium">Neural Connection: </span>$1</div>')
    .replace(/🔠(.*?)(?=\n|$)/g, '<div class="acronym-block bg-yellow-50 dark:bg-yellow-900/30 p-3 rounded-lg my-2"><span class="emoji">🔠</span><span class="font-medium">Memory Acronym: </span>$1</div>')
    .replace(/🎵(.*?)(?=\n|$)/g, '<div class="rhythm-block bg-pink-50 dark:bg-pink-900/30 p-3 rounded-lg my-2"><span class="emoji">🎵</span><span class="font-medium">Rhythm & Rhyme: </span>$1</div>')
    .replace(/🎲(.*?)(?=\n|$)/g, '<div class="pattern-block bg-teal-50 dark:bg-teal-900/30 p-3 rounded-lg my-2"><span class="emoji">🎲</span><span class="font-medium">Pattern Recognition: </span>$1</div>')
    .replace(/🕹️(.*?)(?=\n|$)/g, '<div class="chunking-block bg-lime-50 dark:bg-lime-900/30 p-3 rounded-lg my-2"><span class="emoji">🕹️</span><span class="font-medium">Chunking Method: </span>$1</div>')
    .replace(/🧩(.*?)(?=\n|$)/g, '<div class="concept-map-block bg-orange-50 dark:bg-orange-900/30 p-3 rounded-lg my-2"><span class="emoji">🧩</span><span class="font-medium">Concept Map: </span>$1</div>');
}

/**
 * Formats content sections with enhanced styling for better readability
 * @param content The section content to format
 * @returns Formatted HTML with enhanced styling
 */
export function formatSectionContent(content: string): string {
  if (!content) return '';
  
  // First, process markdown headings with proper HTML conversion and styling
  let formattedContent = content
    // Handle markdown headings and convert to styled HTML
    .replace(/^# ([^#\n]+)/gm, '<h1 class="text-3xl font-bold my-6 bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">$1</h1>')
    .replace(/^## ([^#\n]+)/gm, '<h2 class="text-2xl font-bold my-5 bg-gradient-to-r from-purple-500 to-indigo-500 bg-clip-text text-transparent border-b border-purple-200 dark:border-purple-700 pb-2">$1</h2>')
    .replace(/^### ([^#\n]+)/gm, '<h3 class="text-xl font-bold my-4 bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent">$1</h3>')
    .replace(/^#### ([^#\n]+)/gm, '<h4 class="text-lg font-semibold my-3 text-emerald-600 dark:text-emerald-400 border-l-4 border-emerald-300 dark:border-emerald-600 pl-3">$1</h4>')
    .replace(/^##### ([^#\n]+)/gm, '<h5 class="text-md font-semibold my-2 text-blue-600 dark:text-blue-400">$1</h5>')
    .replace(/^###### ([^#\n]+)/gm, '<h6 class="text-sm font-semibold my-2 text-gray-600 dark:text-gray-400">$1</h6>');
  
  // Apply markdown to HTML conversion for other elements
  formattedContent = markdownToHtml(formattedContent);
  
  // Process special markers
  formattedContent = processSpecialMarkers(formattedContent);
  
  // Format lists and bullet points
  formattedContent = formattedContent
    .replace(/<ul>(.*?)<\/ul>/gs, '<ul class="my-4 space-y-2">$1</ul>')
    .replace(/<li>(.*?)<\/li>/g, '<li class="flex items-start gap-2"><span class="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-purple-400 mt-1.5"></span><span>$1</span></li>');
  
  // Format code blocks
  formattedContent = formattedContent
    .replace(/<pre><code>(.*?)<\/code><\/pre>/gs, '<pre class="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg my-4 overflow-x-auto">$1</pre>');
  
  // Format blockquotes
  formattedContent = formattedContent
    .replace(/<blockquote>(.*?)<\/blockquote>/gs, '<blockquote class="border-l-4 border-purple-300 pl-4 italic my-4">$1</blockquote>');
  
  // Process special symbols and equations (for math content)
  formattedContent = formattedContent
    .replace(/\$\$(.*?)\$\$/g, '<div class="math-block">$1</div>')
    .replace(/\$(.*?)\$/g, '<span class="math-inline">$1</span>');
  
  // Process advanced learning structures
  formattedContent = formattedContent
    .replace(/\[COMPARISON:(.*?):(.*?)\]/g, '<div class="comparison-container flex flex-col md:flex-row gap-3 my-4"><div class="comparison-item flex-1 bg-blue-50 dark:bg-blue-900/30 p-3 rounded-lg">$1</div><div class="comparison-item flex-1 bg-indigo-50 dark:bg-indigo-900/30 p-3 rounded-lg">$2</div></div>')
    .replace(/\[TIMELINE:(.*?)\]/g, '<div class="timeline-container p-4 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/30 dark:to-indigo-900/30 rounded-lg my-4">$1</div>')
    .replace(/\[PROCESS:step:(.*?)\]/g, '<div class="process-step my-2 pl-8 relative before:content-[\'\'] before:absolute before:left-1 before:top-1 before:h-6 before:w-6 before:rounded-full before:bg-purple-100 before:border-2 before:border-purple-300 before:z-10 after:content-[\'\'] after:absolute after:left-3.5 after:top-7 after:h-full after:w-0.5 after:bg-purple-200">$1</div>');
  
  return formattedContent;
}

/**
 * Formats chat messages with enhanced styling
 * @param message The chat message to format
 * @returns Formatted HTML for chat messages
 */
export function formatChatMessage(message: string): string {
  if (!message) return '';
  
  // Apply base formatting
  let formattedMessage = formatSectionContent(message);
  
  // Add chat-specific styling
  formattedMessage = formattedMessage
    .replace(/<p>(.*?)<\/p>/g, '<p class="mb-3 last:mb-0">$1</p>')
    .replace(/(Q:)(.*?)(?=<\/p>|<p>|$)/g, '<span class="font-semibold text-purple-600">Q:</span>$2')
    .replace(/(A:)(.*?)(?=<\/p>|<p>|$)/g, '<span class="font-semibold text-indigo-600">A:</span>$2');
  
  return formattedMessage;
}

/**
 * Formats content specifically for learning presentations
 * @param content The learning content to format
 * @returns Formatted HTML with learning-specific styles
 */
export function formatLearningContent(content: string): string {
  if (!content) return '';
  
  // Apply base formatting first
  let formattedContent = formatSectionContent(content);
  
  // Add learning-specific styling
  formattedContent = formattedContent
    // Concept boxes
    .replace(/\[CONCEPT\](.*?)\[\/CONCEPT\]/gs, '<div class="bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-700 rounded-lg p-4 my-4"><h4 class="font-bold text-purple-700 dark:text-purple-300 mb-2">Key Concept</h4>$1</div>')
    // Example boxes
    .replace(/\[EXAMPLE\](.*?)\[\/EXAMPLE\]/gs, '<div class="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4 my-4"><h4 class="font-bold text-blue-700 dark:text-blue-300 mb-2">Example</h4>$1</div>')
    // Memory aid boxes
    .replace(/\[MEMORY\](.*?)\[\/MEMORY\]/gs, '<div class="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg p-4 my-4"><h4 class="font-bold text-amber-700 dark:text-amber-300 mb-2">Memory Aid</h4>$1</div>')
    // Visual aid boxes
    .replace(/\[VISUAL\](.*?)\[\/VISUAL\]/gs, '<div class="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg p-4 my-4"><h4 class="font-bold text-green-700 dark:text-green-300 mb-2">Visual Aid</h4>$1</div>')
    // Quick quiz boxes
    .replace(/\[QUIZ\](.*?)\[\/QUIZ\]/gs, '<div class="bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700 rounded-lg p-4 my-4"><h4 class="font-bold text-indigo-700 dark:text-indigo-300 mb-2">Quick Quiz</h4>$1</div>')
    // Mnemonic boxes
    .replace(/\[MNEMONIC\](.*?)\[\/MNEMONIC\]/gs, '<div class="bg-pink-50 dark:bg-pink-900/30 border border-pink-200 dark:border-pink-700 rounded-lg p-4 my-4"><h4 class="font-bold text-pink-700 dark:text-pink-300 mb-2">Remember This</h4>$1</div>')
    // Real-world application boxes
    .replace(/\[APPLICATION\](.*?)\[\/APPLICATION\]/gs, '<div class="bg-cyan-50 dark:bg-cyan-900/30 border border-cyan-200 dark:border-cyan-700 rounded-lg p-4 my-4"><h4 class="font-bold text-cyan-700 dark:text-cyan-300 mb-2">Real World Application</h4>$1</div>')
    // New advanced learning formats
    .replace(/\[MIND_PALACE\](.*?)\[\/MIND_PALACE\]/gs, '<div class="bg-violet-50 dark:bg-violet-900/30 border border-violet-200 dark:border-violet-700 rounded-lg p-4 my-4"><h4 class="font-bold text-violet-700 dark:text-violet-300 mb-2">Mind Palace Technique</h4>$1</div>')
    .replace(/\[CHUNKING\](.*?)\[\/CHUNKING\]/gs, '<div class="bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-700 rounded-lg p-4 my-4"><h4 class="font-bold text-emerald-700 dark:text-emerald-300 mb-2">Chunking Strategy</h4>$1</div>')
    .replace(/\[SPACED\](.*?)\[\/SPACED\]/gs, '<div class="bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-700 rounded-lg p-4 my-4"><h4 class="font-bold text-orange-700 dark:text-orange-300 mb-2">Spaced Repetition Plan</h4>$1</div>')
    .replace(/\[COGNITIVE_MAP\](.*?)\[\/COGNITIVE_MAP\]/gs, '<div class="bg-lime-50 dark:bg-lime-900/30 border border-lime-200 dark:border-lime-700 rounded-lg p-4 my-4"><h4 class="font-bold text-lime-700 dark:text-lime-300 mb-2">Cognitive Mapping</h4>$1</div>');

  return formattedContent;
}

/**
 * Formats time in minutes:seconds format
 * @param seconds Total seconds
 * @returns Formatted time string (MM:SS)
 */
export function formatTime(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  
  // Format numbers with leading zeros
  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = String(remainingSeconds).padStart(2, '0');
  
  return `${formattedMinutes}:${formattedSeconds}`;
}

/**
 * Formats mathematical expressions with proper rendering
 * @param content The content with math expressions
 * @returns Formatted HTML for math expressions
 */
export function formatMathExpressions(content: string): string {
  if (!content) return '';
  
  // Apply base formatting first
  let formattedContent = formatHtml(content);
  
  // Handle LaTeX-style math expressions
  formattedContent = formattedContent
    // Display math (centered equations)
    .replace(/\\\[(.*?)\\\]/gs, '<div class="math-display">\\[$1\\]</div>')
    // Inline math
    .replace(/\\\((.*?)\\\)/g, '<span class="math-inline">\\($1\\)</span>')
    // LaTeX environments
    .replace(/\\begin\{(.*?)\}(.*?)\\end\{\1\}/gs, '<div class="math-environment">\\begin{$1}$2\\end{$1}</div>');
  
  // Format fractions, square roots, etc.
  formattedContent = formattedContent
    .replace(/\\frac\{(.*?)\}\{(.*?)\}/g, '<span class="math-frac">\\frac{$1}{$2}</span>')
    .replace(/\\sqrt\{(.*?)\}/g, '<span class="math-sqrt">\\sqrt{$1}</span>')
    .replace(/\\theta/g, '<span class="math-symbol">\\theta</span>')
    .replace(/\\neq/g, '<span class="math-symbol">\\neq</span>');
  
  // Format matrices and other block elements
  formattedContent = formattedContent
    .replace(/\\begin\{matrix\}(.*?)\\end\{matrix\}/gs, 
             '<div class="math-matrix">\\begin{matrix}$1\\end{matrix}</div>');
  
  return formattedContent;
}

/**
 * Formats text using memory-enhancing techniques like dual-coding, chunking, etc.
 * @param content The content to format with memory techniques
 * @returns Formatted HTML with memory enhancements
 */
export function formatMemoryEnhanced(content: string): string {
  if (!content) return '';
  
  // Apply base formatting first
  let formattedContent = formatHtml(content);
  
  // Apply dual-coding (combining verbal and visual elements)
  formattedContent = formattedContent
    .replace(/\[DC:(.*?):(.*?)\]/g, 
      '<div class="dual-coding-container bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 p-4 rounded-lg my-3">' +
        '<div class="dual-coding-visual border-b border-purple-100 dark:border-purple-700 pb-2 mb-2 font-medium text-purple-700 dark:text-purple-300">$1</div>' +
        '<div class="dual-coding-verbal">$2</div>' +
      '</div>');
  
  // Apply chunking technique (grouping information into manageable units)
  formattedContent = formattedContent
    .replace(/\[CHUNK:(.*?):(.*?)\]/g,
      '<div class="chunking-container my-3">' +
        '<div class="chunking-title font-semibold text-indigo-600 dark:text-indigo-400">$1</div>' +
        '<div class="chunking-content bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-md mt-1">$2</div>' +
      '</div>');
  
  // Apply interleaving (mixing related but distinct topics)
  formattedContent = formattedContent
    .replace(/\[INTERLEAVE:(.*?):(.*?):(.*?)\]/g,
      '<div class="interleaving-container grid grid-cols-1 md:grid-cols-3 gap-3 my-4">' +
        '<div class="interleaving-title col-span-1 md:col-span-3 font-medium text-teal-600 dark:text-teal-400">$1</div>' +
        '<div class="interleaving-content-1 bg-teal-50 dark:bg-teal-900/20 p-3 rounded-md">$2</div>' +
        '<div class="interleaving-content-2 bg-emerald-50 dark:bg-emerald-900/20 p-3 rounded-md">$3</div>' +
      '</div>');

  // Apply spaced repetition markers
  formattedContent = formattedContent
    .replace(/\[SR:(.*?):(.*?)\]/g,
      '<div class="spaced-repetition-item relative bg-amber-50 dark:bg-amber-900/20 p-3 pl-12 rounded-md my-3">' +
        '<div class="absolute left-3 top-3 px-2 py-1 bg-amber-200 dark:bg-amber-700 rounded text-xs font-medium text-amber-800 dark:text-amber-200">Review: $1</div>' +
        '<div class="spaced-repetition-content pt-2">$2</div>' +
      '</div>');
      
  // Apply elaboration technique (connecting to existing knowledge)
  formattedContent = formattedContent
    .replace(/\[ELABORATE:(.*?):(.*?)\]/g,
      '<div class="elaboration-container my-3">' +
        '<div class="elaboration-base bg-violet-50 dark:bg-violet-900/20 p-3 rounded-t-md border-b border-violet-200 dark:border-violet-700">$1</div>' +
        '<div class="elaboration-connection bg-violet-100/50 dark:bg-violet-800/20 p-3 rounded-b-md italic text-violet-700 dark:text-violet-300">' +
          '<span class="text-sm font-semibold block mb-1">Connect this with:</span>$2' +
        '</div>' +
      '</div>');

  // Apply retrieval practice prompts
  formattedContent = formattedContent
    .replace(/\[RETRIEVE:(.*?):(.*?)\]/g,
      '<div class="retrieval-practice-container bg-blue-50 dark:bg-blue-900/20 p-3 rounded-md my-3">' +
        '<div class="retrieval-question font-medium text-blue-700 dark:text-blue-300 mb-2">$1</div>' +
        '<div class="retrieval-answer hidden opacity-0 transition-opacity duration-300 hover:opacity-100 focus:opacity-100 bg-white/50 dark:bg-gray-800/50 p-2 rounded cursor-help" onclick="this.classList.toggle(\'hidden\')">' +
          '<span class="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Answer:</span>$2' +
        '</div>' +
        '<button class="text-xs text-blue-600 dark:text-blue-400 mt-2 hover:underline" onclick="this.previousElementSibling.classList.toggle(\'hidden\')">Reveal answer</button>' +
      '</div>');

  return formattedContent;
}

/**
 * Formats content using advanced Dual N-Back memory technique structure
 * @param content The content to format using Dual N-Back structure
 * @returns Formatted HTML with Dual N-Back memory structure
 */
export function formatDualNBack(content: string): string {
  if (!content) return '';
  
  let formattedContent = formatHtml(content);
  
  // Create a dual n-back memory training structure
  formattedContent = formattedContent
    .replace(/\[DUAL_NBACK:(.*?):(.*?):(.*?)\]/g,
      '<div class="dual-nback-container bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg my-4 border border-purple-200 dark:border-purple-700">' +
        '<h4 class="font-bold text-purple-700 dark:text-purple-300 mb-2">Dual N-Back Memory Exercise</h4>' +
        '<div class="dual-nback-visual bg-white dark:bg-gray-900 p-3 rounded-md mb-3">' +
          '<div class="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Visual Pattern:</div>$1' +
        '</div>' +
        '<div class="dual-nback-auditory bg-white dark:bg-gray-900 p-3 rounded-md mb-3">' +
          '<div class="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Auditory Pattern:</div>$2' +
        '</div>' +
        '<div class="dual-nback-integration bg-purple-50 dark:bg-purple-900/20 p-3 rounded-md">' +
          '<div class="text-sm font-medium text-purple-600 dark:text-purple-400 mb-1">Integration Technique:</div>$3' +
        '</div>' +
      '</div>');
      
  return formattedContent;
}

/**
 * Formats content with stress-reducing cognitive bias awareness
 * @param content The content to format with cognitive bias awareness
 * @returns Formatted HTML with bias awareness elements
 */
export function formatCognitiveDebiasing(content: string): string {
  if (!content) return '';
  
  let formattedContent = formatHtml(content);
  
  // Format cognitive debiasing elements
  formattedContent = formattedContent
    .replace(/\[BIAS:(.*?):(.*?)\]/g,
      '<div class="cognitive-bias-container bg-red-50 dark:bg-red-900/20 p-3 rounded-md my-3">' +
        '<div class="bias-name font-medium text-red-700 dark:text-red-300 mb-1">Common Bias: $1</div>' +
        '<div class="bias-mitigation text-gray-700 dark:text-gray-300">$2</div>' +
      '</div>');
      
  return formattedContent;
}
